// Time middleware — converts real-time elapsed into AdvanceDay actions.
// D4: capped at 7 days. Anything beyond is dropped with a heads-up so the
// kid sees a "while you were away" summary rather than silent windfall.

import {
  daysBetween,
  type BuilderContent,
} from '@biz-kid/engine';
import type { GameStoreInstance } from './store.js';

export type CatchupReport = {
  daysAdvanced: number;
  daysSkipped: number; // anything beyond the cap
};

/**
 * Walk wall-clock forward from the saved lastWallClockDayKey to `todayKey`,
 * dispatching AdvanceDay once per elapsed day. Capped at content.tuning.catchupMaxDays.
 */
export function runCatchup(
  store: GameStoreInstance,
  content: BuilderContent,
  todayKey: string,
): CatchupReport {
  const fromKey = store.getState().game.clock.lastWallClockDayKey;
  const elapsed = daysBetween(fromKey, todayKey);
  if (elapsed <= 0) return { daysAdvanced: 0, daysSkipped: 0 };

  const cap = content.tuning.catchupMaxDays;
  const toAdvance = Math.min(elapsed, cap);
  const skipped = elapsed - toAdvance;

  // Build sequential dayKeys. We can't run more than cap days even if more elapsed
  // — but the recorded lastWallClockDayKey at the end must still be today, so the
  // next open doesn't try to re-catch the skipped days. The final AdvanceDay uses
  // todayKey directly; intermediate days use synthesised in-between keys.
  let cursorKey = fromKey;
  for (let i = 1; i <= toAdvance; i++) {
    cursorKey =
      i === toAdvance ? todayKey : addDay(fromKey, i);
    const err = store.getState().dispatch({ kind: 'AdvanceDay', dayKey: cursorKey });
    if (err) {
      // No game-rule failure mode for AdvanceDay today — but treat any error as a
      // hard stop rather than a silent skew.
      throw new Error(`AdvanceDay during catchup failed: ${err.code} — ${err.message}`);
    }
  }

  // If we capped, force lastWallClockDayKey forward to today so we don't re-replay.
  if (skipped > 0) {
    const state = store.getState().game;
    store.getState().replaceState({
      ...state,
      clock: { ...state.clock, lastWallClockDayKey: todayKey },
    });
  }

  return { daysAdvanced: toAdvance, daysSkipped: skipped };
}

function addDay(baseKey: string, days: number): string {
  const d = new Date(`${baseKey}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
